/**
 * roomScenarios — the data behind the permission room.
 *
 * The room is the one thing on these pages that no competitor ships: a visitor
 * picks a role, taps an ask, and watches the platform either do it or refuse
 * it — in the platform's own words. Every DMS vendor sells "ethical walls" as
 * a feature bullet. The phrase that matters is that an ethical wall is only
 * real if a system refuses the access, so this section IS the refusal.
 *
 * WHICH MEANS NOTHING HERE MAY BE AUTHORED. Every role, every permission and
 * every verdict is resolved from the two shipped, CI-gated mirrors:
 *
 *   src/constants/permissionCatalog.data.json     (byte-identical to the
 *                                                  functions copy)
 *   src/constants/roleTemplates.generated.json    (the role templates)
 *
 * and the refusal sentence comes from the byte-for-byte mirror of the
 * platform's own copy. If a permission key is renamed, this module throws at
 * import rather than quietly showing a marketing page a wall that no longer
 * exists — the same discipline zeroDay.ts applies to prices.
 *
 * SCOPE, deliberately: the STANDARD catalog only. The full catalog also carries
 * three INTERNAL partitions — `developer.*`, `phoneline.*` and `company.*` — and
 * a public page must never render any of them. `assertStandardKey` is where that
 * is enforced, and it tests membership of STANDARD_PERMISSION_KEYS_UI rather than
 * naming the partitions, so a fourth partition is excluded the day it is added.
 *
 * D8: nothing here classifies anything. The visitor states who they are by
 * clicking; the room renders. There is no model call and no inference.
 */
import { STANDARD_PERMISSION_KEYS_UI, ALL_PERMISSIONS_UI } from '../../constants/permissionCatalog';
import roleTemplates from '../../constants/roleTemplates.generated.json';

export type RoleKey = 'owner' | 'admin' | 'associate' | 'paralegal' | 'read-only';

export interface RoomRole {
  key: RoleKey;
  /** The shipped display name — "Member", not the `associate` template key. */
  name: string;
  /** The shipped one-line description. Not rewritten for marketing. */
  description: string;
  /**
   * Owner and Admin carry NO grant list: their permissions are derived at
   * runtime from the catalog. The room states their rule instead of pretending
   * to enumerate it.
   */
  derived: boolean;
}

interface RawTemplate {
  templateKey: string;
  name: string;
  description: string;
  derived: boolean;
  grants: string[];
}

const TEMPLATES: RawTemplate[] = (roleTemplates as { templates: RawTemplate[] }).templates;

/** The 8 keys an Admin does NOT get by default — the sharpest fact on the page. */
export const ADMIN_EXCLUDED: readonly string[] = Object.freeze(
  [...((roleTemplates as { adminExcluded?: string[] }).adminExcluded ?? [])],
);

/** Display order, most access first. */
const ROLE_ORDER: RoleKey[] = ['owner', 'admin', 'associate', 'paralegal', 'read-only'];

function templateFor(key: RoleKey): RawTemplate {
  const found = TEMPLATES.find((t) => t.templateKey === key);
  if (!found) {
    throw new Error(
      `roomScenarios: no role template "${key}" in roleTemplates.generated.json. ` +
        'The templates are the source of truth — regenerate the mirror rather than ' +
        'hard-coding a role here.',
    );
  }
  return found;
}

export function rolesInOrder(): RoomRole[] {
  return ROLE_ORDER.map((key) => {
    const t = templateFor(key);
    return { key, name: t.name, description: t.description, derived: t.derived };
  });
}

/**
 * Guards the public-page boundary AND the rename case in one place: a key must
 * exist in the catalog and must not be one of the internal partitions.
 */
function assertStandardKey(permissionKey: string): string {
  if (!STANDARD_PERMISSION_KEYS_UI.has(permissionKey)) {
    throw new Error(
      `roomScenarios: "${permissionKey}" is not a standard permission key. ` +
        'Either it was renamed in the catalog (fix the ask), or it belongs to one of ' +
        'the internal partitions (developer / phone line / Prest0 Inc.), which must ' +
        'never reach a public page.',
    );
  }
  return permissionKey;
}

const LABELS: Map<string, string> = new Map(ALL_PERMISSIONS_UI.map((p) => [p.key, p.label]));

/** The shipped label for a key, for "this ask needs …" copy. */
export function permissionLabel(permissionKey: string): string {
  return LABELS.get(permissionKey) ?? permissionKey;
}

/**
 * What an Admin does NOT hold, as the platform's own labels.
 *
 * Two surfaces had hand-typed versions of this list — the room and /pricing —
 * and a third was about to. Both were WRONG the same way: each named
 * transferring, deleting and the payment methods, and neither named the
 * usage-limit delegation key or the two org-observability keys, which joined
 * `adminExcluded` later and separately as those surfaces shipped. An
 * enumeration that goes stale on a page selling "an ethical wall is only real
 * if a system refuses the access" is the exact failure this work exists to
 * correct, so the list is read from the mirror instead of written down.
 *
 * A key with no catalog entry falls back to its own name rather than being
 * dropped: an admin exclusion silently missing from the sentence is worse
 * than an ugly one.
 */
export function adminExcludedLabels(): string[] {
  return ADMIN_EXCLUDED.map((key) => permissionLabel(key));
}

export interface RoomAsk {
  id: string;
  /** What the visitor taps — phrased as a person would say it in a thread. */
  label: string;
  /** The permission the platform actually checks. */
  permissionKey: string;
  /**
   * The activity as the refusal names it. Where the platform already has a
   * label for the gated activity, this IS that label, so the sentence the room
   * shows is the sentence a real person would receive. Unmapped labels are
   * spoken as-is by design — see permissionRefusalCopy.
   */
  activityLabel: string;
}

/**
 * Eight asks a law firm recognises. Chosen so the five roles separate cleanly:
 * Read-Only can do one of them, Assistant four, Member five, Admin and Owner
 * all eight. Every permissionKey is verified against the catalog at import.
 */
export const ROOM_ASKS: readonly RoomAsk[] = Object.freeze([
  {
    id: 'read-file',
    label: 'read the case file',
    permissionKey: assertStandardKey('documents.view'),
    activityLabel: 'reading documents',
  },
  {
    id: 'translate',
    label: 'translate this contract into Spanish',
    permissionKey: assertStandardKey('tools.translation.use'),
    activityLabel: 'translation',
  },
  {
    id: 'research',
    label: 'run deep research on the statute',
    permissionKey: assertStandardKey('tools.deep-research.use'),
    activityLabel: 'deep research',
  },
  {
    id: 'draft',
    label: 'draft the demand letter',
    permissionKey: assertStandardKey('tools.document-drafting.use'),
    activityLabel: 'document drafting',
  },
  {
    id: 'browse',
    label: 'check the county portal for a filing',
    permissionKey: assertStandardKey('tools.browser.use'),
    activityLabel: 'browsing',
  },
  {
    id: 'email',
    label: 'email the client the update',
    permissionKey: assertStandardKey('tools.email.send'),
    activityLabel: 'sending email',
  },
  {
    id: 'invite',
    label: 'invite a paralegal to the workspace',
    permissionKey: assertStandardKey('team.invite'),
    activityLabel: 'inviting members',
  },
  {
    id: 'billing',
    label: 'change the billing plan',
    permissionKey: assertStandardKey('billing.manage'),
    activityLabel: 'changing billing',
  },
]);

/**
 * Does this role hold this permission?
 *
 * Owner and Admin are DERIVED, so the rule is applied rather than a list read:
 * an Owner holds every standard permission; an Admin holds every standard
 * permission except the eight on `adminExcluded`. That is the actual runtime
 * rule, not a summary of it.
 *
 * `extraGrants` is the custom-role moment: keys the visitor switched on.
 */
export function holds(
  roleKey: RoleKey,
  permissionKey: string,
  extraGrants?: ReadonlySet<string> | null,
): boolean {
  if (extraGrants?.has(permissionKey)) return true;
  if (roleKey === 'owner') return STANDARD_PERMISSION_KEYS_UI.has(permissionKey);
  if (roleKey === 'admin') {
    return STANDARD_PERMISSION_KEYS_UI.has(permissionKey) && !ADMIN_EXCLUDED.includes(permissionKey);
  }
  return templateFor(roleKey).grants.includes(permissionKey);
}

/** How many of the room's asks a role can run — the gradient, computed. */
export function allowedCount(roleKey: RoleKey, extraGrants?: ReadonlySet<string> | null): number {
  return ROOM_ASKS.filter((a) => holds(roleKey, a.permissionKey, extraGrants)).length;
}

/**
 * The one ask worth offering as the custom-role toggle: something the two
 * least-privileged roles are refused, so flipping it visibly changes an
 * answer. Read off the data rather than picked by hand, so it stays true if
 * the templates change.
 */
export function toggleableAskFor(roleKey: RoleKey): RoomAsk | null {
  if (roleKey === 'owner' || roleKey === 'admin') return null;
  return ROOM_ASKS.find((a) => !holds(roleKey, a.permissionKey)) ?? null;
}

/**
 * What an allowed ask looks like happening. Illustration, and labelled as one.
 * Both rooms read this one list: today's (Room.tsx) and the new design's
 * (src/design/pavilion/pages/home/PavilionRoom.tsx). It lives here, with the rest
 * of the room's data, rather than in Room.tsx: importing it from there carried
 * today's room's styled components into the new design's home page chunk.
 */
export const OUTCOMES: Readonly<Record<string, string>> = {
  'read-file': 'opened the matter file — 41 documents, indexed. ask me anything in it.',
  translate: 'translated, and delivered as PDF and Word. the original is untouched.',
  research: 'a cited memo, with the sources listed, as PDF and Word.',
  draft: 'drafted from your own past letters, flagged for your review before it goes anywhere.',
  browse: 'worked the portal in a real browser on your workspace computer. you watched it happen.',
  email: 'written and held — it sends when you authorize it, never before.',
  invite: 'invitation sent, with the role you chose.',
  billing: 'plan change previewed, with the prorated amount, before anything is charged.',
};
